/**
 * Opening the local folder in Windows File Explorer.
 *
 * A web page may not start programs, so a small helper is set up once on the computer:
 * Windows hands it noodle-folder: links and it asks File Explorer to open the folder.
 * The helper only opens folders inside the one chosen during setup, and never runs a file:
 * a file is only ever selected in its folder.
 */

export const FOLDER_HELPER_SCHEME = 'noodle-folder';
export const FOLDER_HELPER_SETUP_NAME = 'noodle-folder-helper.cmd';
const READY_KEY = 'noodle:folderHelperReady';

/** The helper is for Windows; other systems keep the browser's own file window. */
export const canUseFolderHelper = (): boolean =>
    typeof navigator !== 'undefined' && /Windows/i.test(navigator.userAgent);

/** The link the helper opens: the chosen folder by name, the folders inside it, and maybe a file to select. */
export const folderHelperUrl = (root: string, folders: string[], file?: string): string => {
    const query = [`root=${encodeURIComponent(root)}`, `path=${encodeURIComponent(folders.join('/'))}`];
    if (file) query.push(`file=${encodeURIComponent(file)}`);
    return `${FOLDER_HELPER_SCHEME}:open?${query.join('&')}`;
};

export const isFolderHelperReady = (): boolean => {
    try {
        return localStorage.getItem(READY_KEY) === '1';
    } catch {
        return false;
    }
};

export const setFolderHelperReady = (ready: boolean): void => {
    try {
        if (ready) localStorage.setItem(READY_KEY, '1');
        else localStorage.removeItem(READY_KEY);
    } catch { /* storage blocked: the setup steps show again next time */ }
};

let launchedAt = 0;

/** The browser fires beforeunload for a helper link although the page stays open. */
export const launchedFolderHelperRecently = (): boolean => Date.now() - launchedAt < 2000;

export const openWithFolderHelper = (url: string): void => {
    launchedAt = Date.now();
    const link = document.createElement('a');
    link.href = url;
    link.rel = 'noreferrer';
    document.body.appendChild(link);
    link.click();
    link.remove();
};

export const downloadFolderHelperSetup = (folderName: string): void => {
    const url = URL.createObjectURL(new Blob([folderHelperSetupScript(folderName)], { type: 'application/octet-stream' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = FOLDER_HELPER_SETUP_NAME;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
};

/** The helper itself, compiled on the computer during setup so it needs no console window. */
const HELPER_SOURCE = String.raw`using System;
using System.Collections.Generic;
using System.Diagnostics;
using System.IO;
using System.Reflection;
using System.Runtime.InteropServices;
using System.Windows.Forms;

[assembly: AssemblyTitle("Noodle Studio Folder Helper")]
[assembly: AssemblyProduct("Noodle Studio")]

public static class NoodleFolderHelper
{
    const string Scheme = "noodle-folder:";

    [DllImport("user32.dll")]
    static extern bool AllowSetForegroundWindow(int processId);

    [STAThread]
    public static int Main(string[] args)
    {
        try { return Run(args); }
        catch (Exception e) { Tell("The folder could not be opened.\n\n" + e.Message); return 1; }
    }

    static int Run(string[] args)
    {
        if (args.Length == 0 || !args[0].StartsWith(Scheme, StringComparison.OrdinalIgnoreCase)) return 1;
        string link = args[0].Substring(Scheme.Length);
        int start = link.IndexOf('?');
        Dictionary<string, string> query = ParseQuery(start < 0 ? "" : link.Substring(start + 1));
        string root = Value(query, "root");
        string file = Value(query, "file");

        List<string> parts = new List<string>();
        foreach (string part in Value(query, "path").Split('/'))
        {
            if (part.Length == 0) continue;
            if (!IsPlainName(part)) return 1;
            parts.Add(part);
        }
        if (file.Length > 0 && !IsPlainName(file)) return 1;

        string folder = FindFolder(root);
        if (folder == null)
        {
            Tell("Noodle Studio saves into a folder named \"" + root + "\", but the helper was set up with another folder.\n\nRun noodle-folder-helper.cmd again and choose that folder.");
            return 1;
        }

        // The deepest of the folders that still exists, never outside the chosen folder
        string target = folder;
        bool reached = true;
        foreach (string part in parts)
        {
            string next = Path.Combine(target, part);
            if (!Directory.Exists(next)) { reached = false; break; }
            target = next;
        }
        target = Path.GetFullPath(target);
        if (!IsInside(target, folder)) return 1;

        string arguments = Quote(target);
        if (reached && file.Length > 0)
        {
            string chosen = Path.Combine(target, file);
            if (File.Exists(chosen)) arguments = "/select," + Quote(chosen);
        }
        string trace = Environment.GetEnvironmentVariable("NOODLE_FOLDER_HELPER_TRACE");
        if (!string.IsNullOrEmpty(trace)) { File.WriteAllText(trace, arguments); return 0; }

        // Explorer may bring its window to the front: this helper was started by a click
        AllowSetForegroundWindow(-1);
        string explorer = Path.Combine(Environment.GetFolderPath(Environment.SpecialFolder.Windows), "explorer.exe");
        Process.Start(new ProcessStartInfo(explorer, arguments) { UseShellExecute = false });
        return 0;
    }

    static Dictionary<string, string> ParseQuery(string query)
    {
        Dictionary<string, string> values = new Dictionary<string, string>(StringComparer.OrdinalIgnoreCase);
        foreach (string pair in query.Split('&'))
        {
            int equals = pair.IndexOf('=');
            if (equals <= 0) continue;
            values[pair.Substring(0, equals)] = Uri.UnescapeDataString(pair.Substring(equals + 1));
        }
        return values;
    }

    static string Value(Dictionary<string, string> values, string key)
    {
        string value;
        return values.TryGetValue(key, out value) ? value : "";
    }

    /** One folder or file name: no separators, quotes or other characters Windows refuses, and not "." or "..". */
    static bool IsPlainName(string name)
    {
        if (name.Length == 0 || name.Length > 255) return false;
        if (name.Trim().Trim('.').Length == 0) return false;
        return name.IndexOfAny(Path.GetInvalidFileNameChars()) < 0;
    }

    /** The folder chosen during setup with this name; the newest choice wins. */
    static string FindFolder(string root)
    {
        string list = Path.Combine(AppDomain.CurrentDomain.BaseDirectory, "folders.txt");
        if (!File.Exists(list)) return null;
        foreach (string line in File.ReadAllLines(list))
        {
            string folder = line.Trim();
            if (folder.Length == 0 || !Directory.Exists(folder)) continue;
            folder = Path.GetFullPath(folder);
            if (root.Length == 0 || Path.GetFileName(folder.TrimEnd('\\')).Equals(root, StringComparison.OrdinalIgnoreCase)) return folder;
        }
        return null;
    }

    static bool IsInside(string path, string folder)
    {
        string inner = path.TrimEnd('\\');
        string outer = Path.GetFullPath(folder).TrimEnd('\\');
        return inner.Equals(outer, StringComparison.OrdinalIgnoreCase) || inner.StartsWith(outer + "\\", StringComparison.OrdinalIgnoreCase);
    }

    // A path ending in a backslash would escape its closing quote
    static string Quote(string path)
    {
        return "\"" + (path.EndsWith("\\") ? path + "." : path) + "\"";
    }

    static void Tell(string message)
    {
        string trace = Environment.GetEnvironmentVariable("NOODLE_FOLDER_HELPER_TRACE");
        if (!string.IsNullOrEmpty(trace)) { File.WriteAllText(trace, "MESSAGE " + message); return; }
        MessageBox.Show(message, "Noodle Studio", MessageBoxButtons.OK, MessageBoxIcon.Information);
    }
}`;

/**
 * The setup file: a batch file that runs its own PowerShell part. It compiles the helper into
 * the user's AppData, remembers the chosen folder, and registers noodle-folder: links for this
 * Windows user only. Run again, it adds another folder or removes everything.
 */
export const folderHelperSetupScript = (folderName: string): string => {
    const expected = folderName.replace(/'/g, "''");
    const lines = [
        '<# : Noodle Studio folder helper setup. Double-click this file to run it.',
        '@echo off',
        'set "NOODLE_HELPER_SETUP=%~f0"',
        'powershell.exe -NoProfile -ExecutionPolicy Bypass -Command "iex ([IO.File]::ReadAllText($env:NOODLE_HELPER_SETUP))"',
        'echo.',
        'pause',
        'exit /b',
        '#>',
        '',
        `$expected = '${expected}'`,
        '',
        'function Install-NoodleFolderHelper {',
        "    $dir = Join-Path $env:LOCALAPPDATA 'NoodleStudio\\FolderHelper'",
        "    $exe = Join-Path $dir 'NoodleFolderHelper.exe'",
        "    $list = Join-Path $dir 'folders.txt'",
        `    $key = 'HKCU:\\Software\\Classes\\${FOLDER_HELPER_SCHEME}'`,
        '',
        "    Write-Host ''",
        "    Write-Host 'Noodle Studio folder helper' -ForegroundColor Cyan",
        "    Write-Host 'Lets Noodle Studio open its folder in File Explorer. It opens only folders inside the one you choose.'",
        "    Write-Host ''",
        "    $answer = Read-Host 'Press Enter to set it up, or type R and press Enter to remove it'",
        "    if ($answer -match '^\\s*[rR]') {",
        '        if (Test-Path $key) { Remove-Item -Path $key -Recurse -Force }',
        '        if (Test-Path $dir) { Remove-Item -Path $dir -Recurse -Force }',
        "        Write-Host 'The helper was removed.' -ForegroundColor Green",
        '        return',
        '    }',
        '',
        '    Add-Type -AssemblyName System.Windows.Forms',
        '    $owner = New-Object System.Windows.Forms.Form -Property @{ TopMost = $true }',
        '    $dialog = New-Object System.Windows.Forms.FolderBrowserDialog',
        '    $dialog.Description = "Choose the folder Noodle Studio saves into: $expected"',
        '    $dialog.ShowNewFolderButton = $false',
        '    $picked = $dialog.ShowDialog($owner)',
        '    $owner.Dispose()',
        '    if ($picked -ne [System.Windows.Forms.DialogResult]::OK) {',
        "        Write-Host 'No folder was chosen, so nothing was changed.'",
        '        return',
        '    }',
        '    $folder = $dialog.SelectedPath',
        '    $name = Split-Path -Path $folder -Leaf',
        '    if ($expected -and $name -ne $expected) {',
        `        Write-Host "Noodle Studio's folder is called '$expected', but you chose '$name'. Nothing was changed: run this file again and choose '$expected'." -ForegroundColor Yellow`,
        '        return',
        '    }',
        '',
        '    New-Item -ItemType Directory -Force -Path $dir | Out-Null',
        "    $source = @'",
        HELPER_SOURCE,
        "'@",
        '    if (Test-Path $exe) { Remove-Item -Path $exe -Force }',
        '    Add-Type -TypeDefinition $source -ReferencedAssemblies System.Windows.Forms -OutputAssembly $exe -OutputType WindowsApplication',
        '',
        '    # Newest first: the helper uses the newest folder with a matching name',
        '    $folders = @($folder)',
        '    if (Test-Path $list) { $folders += @(Get-Content -LiteralPath $list | Where-Object { $_ -and $_ -ne $folder }) }',
        '    Set-Content -LiteralPath $list -Value $folders -Encoding UTF8',
        '',
        "    New-Item -Path $key -Force -Value 'URL:Noodle Studio Folder Helper' | Out-Null",
        "    New-ItemProperty -Path $key -Name 'URL Protocol' -Value '' -PropertyType String -Force | Out-Null",
        `    New-Item -Path "$key\\shell\\open\\command" -Force -Value ('"' + $exe + '" "%1"') | Out-Null`,
        '',
        "    Write-Host ''",
        '    Write-Host "Done. Noodle Studio can now open $folder in File Explorer." -ForegroundColor Green',
        "    Write-Host 'Go back to the app and click Open in File Explorer. The first time, the browser asks whether to open the helper: tick Always allow.'",
        '}',
        '',
        'try {',
        '    $ErrorActionPreference = \'Stop\'',
        '    Install-NoodleFolderHelper',
        '} catch {',
        '    Write-Host "Setup did not finish: $($_.Exception.Message)" -ForegroundColor Red',
        '}',
        '',
    ];
    // Batch files want Windows line endings
    return lines.join('\n').replace(/\r?\n/g, '\r\n');
};
